import type { ReactNode, ComponentPropsWithoutRef, ElementType } from 'react';
import { forwardRef } from 'react';
import { styled, css } from 'styled-components';
import { isRecord } from '@wistia/type-guards';
import { makePolymorphic } from '../../private/helpers/makePolymorphic';
import type { Spacings } from '../../css/designTokens/spacing';
import type { ResponsiveObject } from '../../private/hooks/useResponsiveProp/useResponsiveProp';
import { useResponsiveProp } from '../../private/hooks/useResponsiveProp/useResponsiveProp';

const DEFAULT_ELEMENT = 'div';

type StyledGridProps = {
  $maxColumns: number | 'auto';
  $minChildWidth: number;
  $columnGap: Spacings;
  $rowGap: Spacings;
  $expandItems: boolean;
};

/**
 * Determines the grid template columns CSS value based on the grid configuration
 */
const getGridTemplateColumns = (
  maxColumns: number | 'auto',
  minChildWidth: number | 'auto',
  expandItems: boolean,
) => {
  if (minChildWidth === 'auto' && maxColumns === 'auto') {
    return css`
      grid-template-columns: repeat(auto-fit, minmax(0, 1fr));
    `;
  }
  const gridMode = expandItems ? 'auto-fit' : 'auto-fill';
  const minChildWidthValue = minChildWidth === 'auto' ? 0 : minChildWidth;

  if (maxColumns === 'auto') {
    return css`
      grid-template-columns: repeat(${gridMode}, minmax(${minChildWidthValue}px, 1fr));
    `;
  }

  return css`
    /* Adapted from https://9elements.com/blog/building-a-rock-solid-auto-grid/ */
    --wui-grid-total-column-gap-width: calc(${maxColumns - 1} * var(--wui-grid-column-gap));
    --wui-grid-max-column-width: calc(
      (100% - var(--wui-grid-total-column-gap-width)) / ${maxColumns}
    );

    grid-template-columns: repeat(
      ${gridMode},
      minmax(max(${minChildWidthValue}px, var(--wui-grid-max-column-width)), 1fr)
    );
  `;
};

const StyledGrid = styled.div<StyledGridProps>`
  --wui-grid-column-gap: ${({ $columnGap }) => `var(--wui-${$columnGap})`};
  --wui-grid-row-gap: ${({ $rowGap }) => `var(--wui-${$rowGap})`};

  display: grid;
  gap: var(--wui-grid-column-gap) var(--wui-grid-row-gap);
  width: 100%;

  ${({ $maxColumns, $minChildWidth, $expandItems }) =>
    getGridTemplateColumns($maxColumns, $minChildWidth, $expandItems)}
`;

type Gap = {
  column: Spacings;
  row: Spacings;
};

type ResponsiveGap = ResponsiveObject<Gap | Spacings>;

export type GridProps = ComponentPropsWithoutRef<'div'> & {
  /**
   * The content to be displayed in the grid
   */
  children: ReactNode;
  /**
   * When minChildWidth is 'auto', this will be the number of columns, otherwise it will be the maximum number of columns.
   */
  columns?: ResponsiveObject<number | 'auto'> | number | 'auto';
  /**
   * Sets the minimum width for each grid item.
   */
  minChildWidth?: ResponsiveObject<number | 'auto'> | number | 'auto';
  /**
   * Controls the spacing between grid items.
   */
  gap?: Gap | ResponsiveGap | Spacings;
  /**
   * Controls whether grid items expand to fill available space when there are fewer items than columns.
   * When true, items grow to fill space. When false, the grid will auto fill the additional space with empty tracks.
   */
  expandItems?: boolean;
  // This will inherit the jsdoc block from PolymorphicComponent
  renderAs?: ElementType;
};

/**
 * A flexible grid component that can either create a fixed number of columns
 * or an auto-filling grid based on the minimum child width.
 */
const GridComponent = forwardRef<typeof DEFAULT_ELEMENT, GridProps>(
  (
    {
      children,
      columns = 'auto',
      minChildWidth = 'auto',
      gap = 'space-02',
      expandItems = false,
      renderAs,
      ...props
    },
    ref,
  ) => {
    const responsiveGap = useResponsiveProp(gap);
    const { column, row } = isRecord(responsiveGap)
      ? responsiveGap
      : { column: responsiveGap, row: responsiveGap };
    const responsiveColumns = useResponsiveProp(columns);
    const responsiveMinChildWidth = useResponsiveProp(minChildWidth);

    return (
      <StyledGrid
        ref={ref}
        $columnGap={column}
        $expandItems={expandItems}
        $maxColumns={responsiveColumns}
        $minChildWidth={responsiveMinChildWidth}
        $rowGap={row}
        as={renderAs ?? DEFAULT_ELEMENT}
        {...props}
      >
        {children}
      </StyledGrid>
    );
  },
);

GridComponent.displayName = 'Grid';

// @ts-expect-error makePolymorphic is causing issues with types in R19
export const Grid = makePolymorphic<typeof DEFAULT_ELEMENT, GridProps>(GridComponent);
